import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  CircularProgress,
  Button,
  TextField,
  ThemeProvider,
  Select,
  MenuItem,
  useTheme,
  FormHelperText,
  Box,
  Checkbox,
  FormControlLabel,
  Chip,
  Typography,
  Divider,
} from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { Add, Remove, Send } from "@mui/icons-material";
import { Office365GroupContext } from "../../context/Office365GroupContext";
import { ModuleContext } from "../../context/ModuleContext";
import { InputTheme } from "../../themes/InputTheme";
import { CreateGroupConfirm } from "../../operations/office365_group/CreateGroupConfirm";

const WSU_RED = "#A60F2D";

export const Office365CreateGroupTable = () => {
  const [state, setState] = useState({
    error: null,
    displayName: "",
    description: "",
    visibility: "Private",
    mailEnabled: true,
    mailNickname: "",
    hiddenFromAddress: false,
    containsStudentData: false,
    infersStudentStatus: false,
    syncOwnersToMembers: true,
    owners: [],
    members: [],
    comment: "",
    waiting: false,
  });

  const [proposedOwner, setProposedOwner] = useState("");
  const [proposedMember, setProposedMember] = useState("");

  const { authState } = useOktaAuth();
  const { setGroupModule } = useContext(Office365GroupContext);
  const { setModule } = useContext(ModuleContext);
  const outerTheme = useTheme();

  const aliasRegex = /^[A-Za-z0-9]+(\.[A-Za-z0-9]+)+$/;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const mustBePrivate = useMemo(
      () => state.containsStudentData || state.infersStudentStatus,
      [state.containsStudentData, state.infersStudentStatus]
  );

  const isFormValid = useMemo(
      () =>
          state.displayName &&
          state.mailNickname &&
          aliasRegex.test(state.mailNickname) &&
          state.owners.length >= 2 &&
          state.comment,
      [state]
  );

  const helperTextAlias = useMemo(() => {
    if (!state.mailNickname) return "";
    return aliasRegex.test(state.mailNickname)
        ? ""
        : "Invalid format: Use alphanumeric name with periods only (e.g., example.name)";
  }, [state.mailNickname]);

  const isAliasInvalid = !!state.mailNickname && !aliasRegex.test(state.mailNickname);

  const helperTextOwners = useMemo(() => {
    if (!proposedOwner) return "";
    if (!emailRegex.test(proposedOwner)) return "Please provide a valid WSU email.";
    if (state.owners.includes(proposedOwner)) return "User is already an owner.";
    return "";
  }, [proposedOwner, state.owners]);

  const isOwnerValid = useMemo(
      () =>
          proposedOwner &&
          emailRegex.test(proposedOwner) &&
          !state.owners.includes(proposedOwner),
      [proposedOwner, state.owners]
  );

  const helperTextMembers = useMemo(() => {
    if (!proposedMember) return "";
    if (!emailRegex.test(proposedMember)) return "Please provide a valid WSU email.";
    if (state.members.includes(proposedMember)) return "User is already a member.";
    return "";
  }, [proposedMember, state.members]);

  const isMemberValid = useMemo(
      () =>
          proposedMember &&
          emailRegex.test(proposedMember) &&
          !state.members.includes(proposedMember),
      [proposedMember, state.members]
  );

  const addOwner = () => {
    setState((prev) => ({
      ...prev,
      owners: [...prev.owners, proposedOwner],
      members: prev.syncOwnersToMembers
          ? [...prev.members, proposedOwner]
          : prev.members,
    }));
    setProposedOwner("");
  };

  const removeOwner = (owner) => {
    setState((prev) => ({
      ...prev,
      owners: prev.owners.filter((o) => o !== owner),
      members: prev.syncOwnersToMembers
          ? prev.members.filter((m) => m !== owner)
          : prev.members,
    }));
  };

  const addMember = () => {
    setState((prev) => ({
      ...prev,
      members: [...prev.members, proposedMember],
    }));
    setProposedMember("");
  };

  const removeMember = (member) => {
    // Prevent removing synced owners from the members list directly
    if (state.syncOwnersToMembers && state.owners.includes(member)) return;
    setState((prev) => ({
      ...prev,
      members: prev.members.filter((m) => m !== member),
    }));
  };

  const handleSyncToggle = (e) => {
    const checked = e.target.checked;
    setState((prev) => ({
      ...prev,
      syncOwnersToMembers: checked,
      // When checked: backfill owners into members
      // When unchecked: remove owners from members list
      members: checked
          ? [...new Set([...prev.members, ...prev.owners])]
          : prev.members.filter((m) => !prev.owners.includes(m)),
    }));
  };

  const createGroup = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    let finalVisibility = state.visibility;
    if (state.infersStudentStatus) finalVisibility = "HiddenMembership";
    else if (state.containsStudentData) finalVisibility = "Private";

    try {
      const res = await fetch(
          `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/groups/office365/create`,
          {
            method: "POST",
            headers: {
              Authorization: authState.accessToken.accessToken,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              displayName: state.displayName,
              description: state.description,
              mailEnabled: state.mailEnabled,
              mailNickname: state.mailNickname,
              visibility: finalVisibility,
              "hiddenFromAddressList?": state.hiddenFromAddress,
              owners: state.owners,
              members: state.members,
              callerId: authState.accessToken.claims.sub,
              comment: state.comment,
            }),
          }
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error?.message || "Unknown error");
      }

      const result = await res.json();
      setGroupModule((prev) => ({
        ...prev,
        id: result.id,
        displayName: result.displayName,
        visibility: result.visibility,
      }));

      setModule((prev) => ({
        ...prev,
        isVisible: true,
        isLog: true,
        element: <CreateGroupConfirm />,
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, waiting: false, error }));
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": { borderColor: WSU_RED },
      "&.Mui-focused fieldset": { borderColor: WSU_RED },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
  };

  const selectSx = {
    width: "60%",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: WSU_RED },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: WSU_RED },
  };

  const labelCellSx = {
    fontWeight: 600,
    color: "#444",
    width: "35%",
    fontSize: "0.9rem",
  };

  const chipSx = {
    backgroundColor: "#fdf0f2",
    border: `1px solid ${WSU_RED}`,
    color: WSU_RED,
    fontWeight: 500,
    "& .MuiChip-deleteIcon": {
      color: WSU_RED,
      "&:hover": { color: "#8a0c25" },
    },
  };

  const addButtonSx = {
    backgroundColor: WSU_RED,
    color: "white",
    minWidth: 44,
    px: 1.5,
    mt: 0.25,
    "&:hover": { backgroundColor: "#8a0c25" },
    "&.Mui-disabled": { backgroundColor: "#ddd" },
  };

  if (state.waiting) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  return (
      <ThemeProvider theme={InputTheme(outerTheme)}>
        <Box sx={{ width: "100vw", display: "flex", justifyContent: "center", px: 2, py: 4 }}>
          <TableContainer
              component={Paper}
              elevation={2}
              sx={{ maxWidth: 800, mx: "auto", borderRadius: 2, overflow: "hidden" }}
          >
            {/* Error Banner */}
            {state.error && (
                <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 1.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {state.error?.message}
                  </Typography>
                </Box>
            )}

            {/* ── Section: Group Details ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Group Details
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Alias *</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.mailNickname}
                        onChange={(e) => setState((prev) => ({ ...prev, mailNickname: e.target.value }))}
                        error={isAliasInvalid}
                        placeholder="e.g. team.name"
                        sx={fieldSx}
                    />
                    {helperTextAlias && <FormHelperText error>{helperTextAlias}</FormHelperText>}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Display Name *</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.displayName}
                        onChange={(e) => setState((prev) => ({ ...prev, displayName: e.target.value }))}
                        placeholder="e.g. My Team Name"
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Description</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.description}
                        onChange={(e) => setState((prev) => ({ ...prev, description: e.target.value }))}
                        placeholder="Optional description"
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Privacy & Visibility ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Privacy &amp; Visibility
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Team Type</TableCell>
                  <TableCell>
                    <Select
                        size="small"
                        sx={selectSx}
                        value={state.visibility}
                        onChange={(e) => setState((prev) => ({ ...prev, visibility: e.target.value }))}
                        disabled={mustBePrivate}
                    >
                      <MenuItem value="Public">Public</MenuItem>
                      <MenuItem value="Private">Private</MenuItem>
                    </Select>
                    {mustBePrivate && (
                        <FormHelperText>Locked to Private due to student data settings</FormHelperText>
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Infers Student Status?</TableCell>
                  <TableCell>
                    <Select
                        size="small"
                        sx={selectSx}
                        value={state.infersStudentStatus}
                        onChange={(e) => setState((prev) => ({ ...prev, infersStudentStatus: e.target.value }))}
                    >
                      <MenuItem value={true}>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Contains Student Data?</TableCell>
                  <TableCell>
                    <Select
                        size="small"
                        sx={selectSx}
                        value={state.containsStudentData}
                        onChange={(e) => setState((prev) => ({ ...prev, containsStudentData: e.target.value }))}
                    >
                      <MenuItem value={true}>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Hidden from GAL?</TableCell>
                  <TableCell>
                    <Select
                        size="small"
                        sx={selectSx}
                        value={state.hiddenFromAddress}
                        onChange={(e) => setState((prev) => ({ ...prev, hiddenFromAddress: e.target.value }))}
                    >
                      <MenuItem value={true}>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Owners ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Owners
              </Typography>
              <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>
                At least 2 owners required
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ ...labelCellSx, borderBottom: "none" }}>
                    Sync Owners to Members
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <FormControlLabel
                        control={
                          <Checkbox
                              checked={state.syncOwnersToMembers}
                              onChange={handleSyncToggle}
                              sx={{ color: WSU_RED, "&.Mui-checked": { color: WSU_RED } }}
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.secondary">
                            Automatically add owners to membership
                          </Typography>
                        }
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Add Owner *</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Box flex={1}>
                        <TextField
                            fullWidth
                            size="small"
                            value={proposedOwner}
                            onChange={(e) => setProposedOwner(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && isOwnerValid) addOwner(); }}
                            error={!isOwnerValid && proposedOwner.length > 0}
                            placeholder="Enter WSU email address"
                            sx={fieldSx}
                        />
                        {helperTextOwners && <FormHelperText error>{helperTextOwners}</FormHelperText>}
                      </Box>
                      <Button variant="contained" onClick={addOwner} disabled={!isOwnerValid} sx={addButtonSx}>
                        <Add />
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {state.owners.length > 0 ? (
                <Box sx={{ px: 3, py: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {state.owners.map((owner) => (
                      <Chip
                          key={owner}
                          label={owner}
                          onDelete={() => removeOwner(owner)}
                          deleteIcon={<Remove fontSize="small" />}
                          sx={chipSx}
                      />
                  ))}
                </Box>
            ) : (
                <Box sx={{ px: 3, py: 1.5 }}>
                  <Typography variant="body2" color="text.disabled" fontStyle="italic">
                    No owners added yet
                  </Typography>
                </Box>
            )}

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Members ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Members
              </Typography>
              <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>
                {state.syncOwnersToMembers
                    ? "Owners are automatically added — add additional members below"
                    : "Add members manually"}
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Add Member</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Box flex={1}>
                        <TextField
                            fullWidth
                            size="small"
                            value={proposedMember}
                            onChange={(e) => setProposedMember(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && isMemberValid) addMember(); }}
                            error={!isMemberValid && proposedMember.length > 0}
                            placeholder="Enter WSU email address"
                            sx={fieldSx}
                        />
                        {helperTextMembers && <FormHelperText error>{helperTextMembers}</FormHelperText>}
                      </Box>
                      <Button variant="contained" onClick={addMember} disabled={!isMemberValid} sx={addButtonSx}>
                        <Add />
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {state.members.length > 0 ? (
                <Box sx={{ px: 3, py: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {state.members.map((member) => {
                    const isOwnerSynced = state.syncOwnersToMembers && state.owners.includes(member);
                    return (
                        <Chip
                            key={member}
                            label={member}
                            onDelete={isOwnerSynced ? undefined : () => removeMember(member)}
                            deleteIcon={isOwnerSynced ? undefined : <Remove fontSize="small" />}
                            sx={{
                              ...chipSx,
                              // Slightly muted style for owner-synced chips to distinguish them
                              opacity: isOwnerSynced ? 0.75 : 1,
                            }}
                        />
                    );
                  })}
                </Box>
            ) : (
                <Box sx={{ px: 3, py: 1.5 }}>
                  <Typography variant="body2" color="text.disabled" fontStyle="italic">
                    No members added yet
                  </Typography>
                </Box>
            )}

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Justification ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Justification
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Comment *</TableCell>
                  <TableCell>
                    <TextField
                        required
                        fullWidth
                        size="small"
                        multiline
                        rows={3}
                        value={state.comment}
                        onChange={(e) => setState((prev) => ({ ...prev, comment: e.target.value }))}
                        placeholder="Provide a reason for creating this group"
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* ── Submit ── */}
            <Box sx={{ px: 3, pt: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
              <Button
                  disabled={!isFormValid}
                  onClick={createGroup}
                  variant="contained"
                  endIcon={<Send />}
                  sx={{
                    backgroundColor: WSU_RED,
                    color: "white",
                    px: 4,
                    py: 1.25,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    "&:hover": { backgroundColor: "#8a0c25" },
                    "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
                  }}
              >
                Create Group
              </Button>
            </Box>
          </TableContainer>
        </Box>
      </ThemeProvider>
  );
};